import React, { useContext } from "react";
import Img from "gatsby-image";
import MainBannerSection from "@src/components/partials/index/MainBannerSection";
import Button from "@src/components/Button";
import { ModalContext, ModalType } from "@src/components/modals/ModalController";
import { Box, Flex } from "@src/components/Boxes";
import { FaPlay } from "react-icons/fa";
import { Text } from "@src/components/Text";
import LearnMoreSection from "@src/components/partials/index/LearnMoreSection";
import SEO from "@src/components/SEO";
import { graphql, useStaticQuery } from "gatsby";
import Container from "@src/components/Container";
import { css } from "@emotion/react";
import { mediaQueries } from "@src/styles/theme";
import Translate from "@src/components/translation/Translate";
import MarkedTranslate from "@src/components/translation/MarkedTranslate";

const bigFeatureText = css`
  h2 {
    font-size: 24px;
    font-weight: 800;
    line-height: 1em;
    margin-bottom: 6px;
    margin-top: 0;
    color: #363636;
  }
  a {
    color: #00bcf2;
    font-weight: 600;

    :hover {
      color: #00a8d9;
      outline: 0;
    }
  }
`;

const titleBottom = css`
  text-align: center;
  h2 {
    font-size: 24px;
    font-weight: 800;
    line-height: 1em;
    margin-bottom: 6px;
    margin-top: 0;
    color: #fff;
  }
`;

function LearnMore() {
  const data = useStaticQuery(graphql`
    query {
      directus {
        page_learnmore {
          bottom_feature_image {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 480) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          above_cta_image {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 480) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          big_feature_one_image {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 480) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          big_feature_two_image {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 480) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          big_feature_three_image {
            id
            file {
              childImageSharp {
                fluid(maxWidth: 480) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
          hero_video_id
        }
      }
    }
  `);
  const {
    directus: {
      page_learnmore: {
        bottom_feature_image,
        above_cta_image,
        big_feature_one_image,
        big_feature_two_image,
        big_feature_three_image,
        hero_video_id,
      },
    },
  } = data;

  const modalContext = useContext(ModalContext);

  function openVideoModal() {
    modalContext.showModal(ModalType.VideoModal, { youtubeID: hero_video_id });
  }

  return (
    <Box textAlign="center">
      <SEO
        title="page_titles.learnmore"
        description="page_descriptions.learnmore"
        image="https://static.classdojo.com/img/social_icons/logo-purple-min.png"
        twitter={{
          card: "summary_large_image",
          site: "@classdojo",
          creator: "@classdojo",
          title: "Learn about ClassDojo",
          description: "Build an amazing classroom community with teachers, parents, and students",
          image: "https://static.classdojo.com/img/social_icons/logo-purple-min.png",
        }}
      />
      <MainBannerSection>
        <Button big onClick={openVideoModal} marginTop="25px">
          <Flex alignItems="center">
            <FaPlay />
            <Text my="0" marginLeft="10px">
              <Translate path="directus.page_learnmore.hero_button_text" />
            </Text>
          </Flex>
        </Button>
      </MainBannerSection>

      <Container py="60px">
        <Flex
          width={["100%", "100%", 10 / 12]}
          mx="auto"
          px="15px"
          alignItems="center"
          flexDirection="column"
          css={bigFeatureText}
        >
          <MarkedTranslate path="directus.page_learnmore.big_features_title" />
        </Flex>

        <Box width={["100%", "100%", 10 / 12]} mx="auto" px="15px">
          <Flex flexDirection={["column", "row"]} maxWidth={["415px", "unset"]} mx="auto" paddingTop="60px">
            <Box width={["100%", 5 / 12]}>
              <Img
                fluid={big_feature_one_image.file.childImageSharp.fluid}
                css={css`
                  max-width: 363px;
                  width: 100%;
                  border: 1px solid #e6e1e1;
                  border-radius: 4px;
                  margin-bottom: 20px;
                  margin-right: auto;
                  margin-left: auto;
                  ${mediaQueries[0]} {
                    border: none;
                    margin-bottom: 0;
                  }
                `}
                alt="ClassDojo app on a cellphone"
              />
            </Box>
            <Flex
              width={["100%", 7 / 12]}
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              css={bigFeatureText}
            >
              <MarkedTranslate path="directus.page_learnmore.big_feature_one_text" />
            </Flex>
          </Flex>
        </Box>
      </Container>

      <Box backgroundColor={["white", "#f5f5f3"]}>
        <Container>
          <Box width={["100%", "100%", 10 / 12]} mx="auto" px="15px">
            <Flex
              flexDirection={["column-reverse", "row"]}
              maxWidth={["415px", "unset"]}
              mx="auto"
              backgroundColor={["white", "#f5f5f3"]}
              paddingTop="60px"
            >
              <Flex
                width={["100%", 7 / 12]}
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                css={bigFeatureText}
              >
                <MarkedTranslate path="directus.page_learnmore.big_feature_two_text" css={bigFeatureText} />
              </Flex>
              <Box width={["100%", 5 / 12]}>
                <Img
                  fluid={big_feature_two_image.file.childImageSharp.fluid}
                  css={css`
                    max-width: 363px;
                    width: 100%;
                    border: 1px solid #e6e1e1;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    margin-right: auto;
                    margin-left: auto;
                    ${mediaQueries[0]} {
                      border: none;
                      margin-bottom: 0;
                    }
                  `}
                  alt="ClassDojo app on a cellphone"
                />
              </Box>
            </Flex>
          </Box>
        </Container>
      </Box>

      <Box backgroundColor={["white"]} paddingBottom={["60px", 0]}>
        <Container>
          <Box width={["100%", "100%", 10 / 12]} mx="auto" px="15px">
            <Flex flexDirection={["column", "row"]} maxWidth={["415px", "unset"]} mx="auto" paddingTop="60px">
              <Box width={["100%", 5 / 12]}>
                <Img
                  fluid={big_feature_three_image.file.childImageSharp.fluid}
                  css={css`
                    max-width: 363px;
                    width: 100%;
                    border: 1px solid #e6e1e1;
                    border-radius: 4px;
                    margin-bottom: 20px;
                    margin-right: auto;
                    margin-left: auto;
                    ${mediaQueries[0]} {
                      border: none;
                      margin-bottom: 0;
                    }
                  `}
                  alt="ClassDojo app on a tablet"
                />
              </Box>
              <Flex
                width={["100%", 7 / 12]}
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                css={bigFeatureText}
              >
                <MarkedTranslate path="directus.page_learnmore.big_feature_three_text" />
              </Flex>
            </Flex>
          </Box>
        </Container>
      </Box>

      <Container
        py="60px"
        css={css`
          border-top: 1px solid #dadada;
        `}
      >
        <Flex
          width={["100%", "100%", 10 / 12]}
          maxWidth="499px"
          mx="auto"
          px="15px"
          alignItems="center"
          flexDirection="column"
          css={bigFeatureText}
        >
          <MarkedTranslate path="directus.page_learnmore.bottom_feature_title" />
        </Flex>

        <Box width={["100%", "100%", 10 / 12]} mx="auto" px="15px">
          <Flex flexDirection={["column", "row"]} maxWidth={["415px", "unset"]} mx="auto" paddingTop="60px">
            <Box width={["100%", 5 / 12]} px="15px">
              <Img
                fluid={bottom_feature_image.file.childImageSharp.fluid}
                css={css`
                  max-width: 363px;
                  width: 100%;
                  margin-bottom: 20px;
                  margin-right: auto;
                  margin-left: auto;
                  ${mediaQueries[0]} {
                    margin-bottom: 0;
                  }
                `}
                alt="ClassDojo app on a cellphone"
              />
            </Box>
            <Flex
              width={["100%", 7 / 12]}
              alignItems={["center", "start"]}
              justifyContent="center"
              flexDirection="column"
              textAlign={["center", "left"]}
              px="15px"
              css={[
                bigFeatureText,
                css`
                  p {
                    margin-bottom: 40px;
                  }
                `,
              ]}
            >
              <MarkedTranslate path="directus.page_learnmore.bottom_feature_text" />
            </Flex>
          </Flex>
        </Box>
      </Container>

      <Box backgroundColor={["#656786"]} paddingBottom="50px">
        <Container>
          <Box width={["100%", "100%", 10 / 12]} mx="auto" px="15px">
            <Flex
              flexDirection={["column-reverse", "row"]}
              maxWidth={["415px", "unset"]}
              mx="auto"
              backgroundColor={["#656786"]}
              paddingTop="60px"
            >
              <Flex
                width={["100%", 6 / 12]}
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                color="#fff"
                css={titleBottom}
              >
                <MarkedTranslate path="directus.page_learnmore.above_cta_text" />
                <Button as="a" href="https://classdojo.zendesk.com/hc/en-us/articles/202027949" target="_blank">
                  <Translate path="directus.page_learnmore.above_cta_button_text" />
                </Button>
              </Flex>
              <Box width={["100%", 6 / 12]}>
                <Img
                  fluid={above_cta_image.file.childImageSharp.fluid}
                  css={css`
                    max-width: 421px;
                    width: 100%;
                    margin-bottom: 20px;
                    margin-right: auto;
                    margin-left: auto;
                    ${mediaQueries[0]} {
                      margin-bottom: 0;
                    }
                  `}
                  alt="ClassDojo app screen captures"
                />
              </Box>
            </Flex>
          </Box>
        </Container>
      </Box>

      <LearnMoreSection />
    </Box>
  );
}

export default LearnMore;
